import {EXIF_SCAN_BYTES, imageTarget, isPreparableImageType, jpegOrientation} from '../../core/page/upload.core.js';
import {ImageType} from '../../core/vocabulary.js';

/** JPEG quality of a re-encoded photo; PNG ignores it. */
const ENCODE_QUALITY = 0.92;

/**
 * Prepares a chosen image for upload: a JPEG or PNG that is too large, or a JPEG whose EXIF
 * orientation turns it, is decoded with that orientation applied and re-encoded in its own
 * format at the size {@link imageTarget} gives, keeping its name and modification time. Any
 * other file, or an image that needs no re-encoding, is returned as it is. Never rejects: when
 * the browser cannot decode or encode the image, the original file is returned and the backend
 * decides whether it accepts it.
 * @param {File} file
 * @returns {Promise<File>}
 */
export async function prepareImage(file) {
    if (!isPreparableImageType(file.type)) return file;
    let bitmap = null;
    try {
        const orientation = file.type === ImageType.JPEG
            ? jpegOrientation(new Uint8Array(await file.slice(0, EXIF_SCAN_BYTES).arrayBuffer()))
            : 1;
        bitmap = await createImageBitmap(file, {imageOrientation: 'from-image'});
        const target = imageTarget({type: file.type, width: bitmap.width, height: bitmap.height, size: file.size, orientation});
        if (!target) return file;
        const canvas = document.createElement('canvas');
        canvas.width = target.width;
        canvas.height = target.height;
        const context = canvas.getContext('2d');
        context.imageSmoothingQuality = 'high';
        context.drawImage(bitmap, 0, 0, target.width, target.height);
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, file.type, ENCODE_QUALITY));
        // A zero-size canvas lets the browser free its pixel memory now, which iOS Safari only
        // does late for a canvas that keeps its size.
        canvas.width = 0;
        canvas.height = 0;
        return blob ? new File([blob], file.name, {type: file.type, lastModified: file.lastModified}) : file;
    } catch {
        return file;
    } finally {
        bitmap?.close();
    }
}
